/**
 * /settings — the personal settings area.
 *
 * A tabbed shell. The tabs are ROUTES, so the URL is the source of truth: a
 * direct link, a refresh and the browser back button all land on a valid
 * tab, exactly like the workspace and the board settings (FRIDAY-65).
 *
 *   /settings                  → Profile
 *   /settings/appearance       → Appearance (the themes)
 *   /settings/integrations     → Integrations (MCP, the PM skill, tokens, GitHub)
 *   /settings/notifications    → Notifications
 *   /settings/tokens           → API tokens (a screen inside Integrations)
 *   /settings/github           → GitHub (a screen inside Integrations)
 *
 * Adding a tab is one route (see `routes.tsx`) plus one entry in
 * `SETTINGS_TABS` below.
 *
 * Each tab caption carries the value that section is set to right now — your
 * alias, your theme, how many tokens are live — so the strip reads as a
 * status board and you know where you stand before you open anything.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTheme } from '@/features/theme/ThemeProvider'
import { useAuth } from '@/features/auth/authContext'
import { toast } from '@/features/feedback/toast'
import { api, apiRaw } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Tabs } from '@/components/ui/Tabs'
import { Switch } from '@/components/ui/Switch'
import { CloseSettingsButton } from '@/components/settings/CloseSettingsButton'
import { ThemePreview } from '@/components/settings/ThemePreview'
import { UserAvatar } from '@/components/user/UserAvatar'
import { activeTokenCount, useApiTokens } from '@/features/settings/apiTokens'
import {
  disablePush,
  enablePush,
  permissionState,
} from '@/features/notifications/push-client'
import type { ThemeDefinition } from '@/features/theme/themes'

/**
 * The tab strip. `to` is the route the tab lives at — adding a tab is one
 * entry here plus one route in `routes.tsx`.
 */
export const SETTINGS_TABS = [
  { id: 'profile', label: 'Profile', to: '/settings' },
  { id: 'appearance', label: 'Appearance', to: '/settings/appearance' },
  { id: 'integrations', label: 'Integrations', to: '/settings/integrations' },
  { id: 'notifications', label: 'Notifications', to: '/settings/notifications' },
] as const

const TABS_ID_BASE = 'settings'

export function SettingsLayout() {
  const { user } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const tokens = useApiTokens()

  if (!user) return null

  const activeId = activeTabId(location.pathname)
  const live = activeTokenCount(tokens.data)

  const hints: Record<string, string> = {
    profile: user.alias?.trim() || 'No alias yet',
    appearance: theme.name,
    integrations: live === 0 ? 'No tokens yet' : `${live} live token${live === 1 ? '' : 's'}`,
    notifications: 'In-app only',
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-[13rem] flex-1">
          <h1 className="text-xl font-semibold">Settings</h1>
          <p className="mt-1 text-xs text-[rgb(var(--fg-muted))]">
            Your account, the way Friday looks, and what it talks to.
          </p>
        </div>
        <CloseSettingsButton fallback="/me" />
      </header>

      <Tabs
        variant="caption"
        tabs={SETTINGS_TABS.map((t) => ({ id: t.id, label: t.label, hint: hints[t.id] }))}
        activeId={activeId}
        label="Personal settings sections"
        idBase={TABS_ID_BASE}
        onSelect={(id) => {
          const tab = SETTINGS_TABS.find((t) => t.id === id)
          if (!tab) return
          navigate({ to: tab.to })
        }}
      />

      <div className="card p-5 sm:p-6">
        <Outlet />
      </div>
    </div>
  )
}

/**
 * Which tab the current URL selects. The URL is the source of truth, so a
 * refresh or a back/forward navigation always lands on the same tab. The
 * token and the GitHub screens belong to Integrations. An unknown suffix
 * falls back to Profile instead of rendering an empty screen.
 */
function activeTabId(pathname: string): string {
  const rest = pathname.replace(/^\/settings\/?/, '').replace(/\/+$/, '')
  if (rest === 'tokens' || rest === 'github') return 'integrations'
  const match = SETTINGS_TABS.find((t) => (t.to === '/settings' ? rest === '' : t.to === `/settings/${rest}`))
  return match ? match.id : 'profile'
}

/* ────────────────────────────── Profile ────────────────────────────── */

export function SettingsProfileView() {
  const { user, updateProfile } = useAuth()
  const [alias, setAlias] = useState(user?.alias ?? '')
  const [avatarUrl, setAvatarUrl] = useState(user?.customAvatarUrl ?? '')
  const [saving, setSaving] = useState(false)

  // Re-hydrate when the user changes (e.g. after /me PATCH).
  useEffect(() => {
    setAlias(user?.alias ?? '')
    setAvatarUrl(user?.customAvatarUrl ?? '')
  }, [user?.alias, user?.customAvatarUrl])

  if (!user) return null

  const displayName = user.alias || user.displayName || user.email
  const dirty = alias.trim() !== (user.alias ?? '') || avatarUrl.trim() !== (user.customAvatarUrl ?? '')

  async function save() {
    if (saving) return
    setSaving(true)
    try {
      await updateProfile({
        alias: alias.trim() ? alias.trim() : null,
        customAvatarUrl: avatarUrl.trim() ? avatarUrl.trim() : null,
      })
      toast('Profile updated', { kind: 'success' })
    } catch (e) {
      toast((e as Error).message ?? 'Could not save the profile.', { kind: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <SectionHead
        title="Profile"
        description="How you appear to the people you work with. Leave a field empty to fall back to your account."
      />

      <div className="grid gap-5 sm:grid-cols-[13rem_1fr]">
        <div className="settings-inset flex flex-col items-center gap-3 p-4 text-center">
          <UserAvatar
            name={displayName}
            email={user.email}
            sources={[user.customAvatarUrl, user.avatarUrl]}
            className="h-20 w-20 text-xl"
            pixelSize={160}
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{displayName}</div>
            <div className="mt-0.5 break-all text-[11px] text-[rgb(var(--fg-muted))]">{user.email}</div>
          </div>
        </div>

        <div className="space-y-4">
          <Field label="Alias" hint="Shown in the sidebar and on comments. Falls back to your display name.">
            <div className="flex items-center gap-2">
              <input
                className="input"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g. Maoz"
                maxLength={60}
              />
              {alias && (
                <button
                  type="button"
                  className="btn-ghost shrink-0 whitespace-nowrap text-xs"
                  onClick={() => setAlias('')}
                >
                  Use default
                </button>
              )}
            </div>
          </Field>

          <Field
            label="Avatar URL"
            hint="Square images look best. Leave it blank to use the account avatar, then your Gravatar."
          >
            <div className="flex items-center gap-2">
              <input
                className="input"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://…/avatar.png"
                type="url"
              />
              {avatarUrl && (
                <button
                  type="button"
                  className="btn-ghost shrink-0 whitespace-nowrap text-xs"
                  onClick={() => setAvatarUrl('')}
                >
                  Use default
                </button>
              )}
            </div>
          </Field>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void save()}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
            {!dirty && !saving && (
              <span className="text-[11px] text-[rgb(var(--fg-muted))]">No changes to save.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ──────────────────────────── Appearance ──────────────────────────── */

export function SettingsAppearanceView() {
  const { theme, themes, setTheme } = useTheme()

  return (
    <div>
      <SectionHead
        title="Appearance"
        description="Each theme is a complete design — colours, typeface and corner shape, not just a colour swap. Your choice is saved on this device."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {themes.map((t) => {
          const active = t.id === theme.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTheme(t.id)}
              aria-pressed={active}
              className={cn(
                'flex flex-col overflow-hidden rounded-[var(--radius-md)] border text-left transition-colors',
                active
                  ? 'border-[rgb(var(--accent))]'
                  : 'border-[rgb(var(--border))] hover:border-[rgb(var(--accent))]',
              )}
            >
              <ThemePreview theme={t} />
              <div className="flex flex-1 flex-col gap-1.5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{t.name}</span>
                  {active && (
                    <span className="badge bg-[rgb(var(--accent))] text-[rgb(var(--accent-fg))]">Active</span>
                  )}
                </div>
                <p className="text-[11px] leading-snug text-[rgb(var(--fg-muted))]">{t.description}</p>
                <div className="mt-auto flex flex-wrap gap-1 pt-1.5">
                  <span className="settings-chip">{fontLabel(t)}</span>
                  <span className="settings-chip">{cornerLabel(t)}</span>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** The typeface a theme sets, for the chip under its name. */
function fontLabel(theme: ThemeDefinition): string {
  const first = theme.tokens.fontFamily
    ?.split(',')[0]
    ?.trim()
    .replace(/^['"]|['"]$/g, '')
  return first || 'Inter'
}

/** Whether a theme cuts its corners square or rounds them. */
function cornerLabel(theme: ThemeDefinition): string {
  return theme.tokens.radiusMd === '0' ? 'Square' : 'Rounded'
}

/* ─────────────────────────── Integrations ─────────────────────────── */

const MCP_SNIPPET = `{
  "mcpServers": {
    "friday": {
      "type": "http",
      "url": "https://mcp.friday.thriv.es",
      "headers": { "Authorization": "Bearer \${FRIDAY_API_TOKEN}" }
    }
  }
}`

export function SettingsIntegrationsView() {
  const tokens = useApiTokens()
  const live = activeTokenCount(tokens.data)

  return (
    <div>
      <SectionHead
        title="Integrations"
        description="Connect Friday to the tools you already use. Everything here is optional — Friday works on its own."
      />

      <div className="settings-inset overflow-hidden">
        <IntegrationRow
          title="MCP server"
          description="Give any AI agent a live window into your tasks, comments and sprints."
          badge="Available"
          actions={
            <>
              <code className="kbd hidden sm:inline">mcp.friday.thriv.es</code>
              <button
                type="button"
                className="btn-outline text-xs"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(MCP_SNIPPET)
                    .then(() => toast('.mcp.json snippet copied', { kind: 'success' }))
                    .catch(() => toast('Could not copy — select and copy manually', { kind: 'error' }))
                }}
              >
                Copy .mcp.json
              </button>
            </>
          }
        />

        <IntegrationRow
          title="Product Manager skill"
          description="A Claude skill that manages your tasks, projects, sprints, comments and notebook pages."
          badge="Skill"
          note="Unzip it into a project's .claude/skills/ folder and give the agent a Friday API token."
          actions={<SkillDownloadButton />}
        />

        <IntegrationRow
          title="API tokens"
          description="Scoped keys for agents and integrations. Revoke one and it stops working immediately."
          badge={live === 0 ? 'None yet' : `${live} live`}
          actions={
            <Link to="/settings/tokens" className="btn-outline text-xs">
              Manage tokens
            </Link>
          }
        />

        <IntegrationRow
          title="GitHub"
          description="Link a board to its repository. A pull request that names a task key moves the task to In Review, then to Done after a healthy release."
          badge="Per board"
          actions={
            <Link to="/settings/github" className="btn-outline text-xs">
              Connect GitHub
            </Link>
          }
        />
      </div>
    </div>
  )
}

function IntegrationRow({
  title,
  description,
  badge,
  note,
  actions,
}: {
  title: string
  description: string
  badge: string
  note?: string
  actions: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          <span className="badge border border-[rgb(var(--border))] text-[10px] text-[rgb(var(--fg-muted))]">
            {badge}
          </span>
        </div>
        <p className="mt-1 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">{description}</p>
        {note && (
          <p className="mt-1.5 max-w-prose text-[11px] leading-relaxed text-[rgb(var(--fg-muted))]">{note}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
    </div>
  )
}

/** Downloads the Product Manager skill bundle. */
function SkillDownloadButton() {
  const [downloading, setDownloading] = useState(false)

  async function downloadSkill() {
    if (downloading) return
    setDownloading(true)
    try {
      // `apiRaw` attaches Authorization + handles one refresh-and-retry on 401.
      // A plain <a download href> doesn't include the JWT, so the server's
      // authMiddleware would 401 the request.
      const res = await apiRaw('/api/v1/me/skill/download')
      if (!res.ok) {
        throw new Error(`Download failed: HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'friday-product-manager.zip'
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Defer revoke so Safari has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
      toast('Skill downloaded', { kind: 'success' })
    } catch (e) {
      toast((e as Error).message ?? 'Could not download the skill.', { kind: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <button
      type="button"
      className="btn-primary text-xs"
      onClick={() => void downloadSkill()}
      disabled={downloading}
    >
      {downloading ? 'Downloading…' : 'Download skill (.zip)'}
    </button>
  )
}

/* ─────────────────────────── Notifications ─────────────────────────── */

export function SettingsNotificationsView() {
  const prefs = useNotificationPrefs()

  return (
    <div>
      <SectionHead title="Notifications" description="Where Friday tells you about what changed." />

      <div className="settings-inset overflow-hidden">
        <ChannelRow
          title="In-app"
          description="The bell in the top bar. Friday tells you when someone mentions you in a comment."
          badge="Always on"
        />
        <BrowserChannelRow prefs={prefs} />
        <EmailChannelRow prefs={prefs} />
      </div>

      <p className="mt-4 text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
        Both channels are off until you turn them on. Friday only ever tells you about the things that need
        you — a mention, an assignment, or a comment on a task you watch.
      </p>
    </div>
  )
}

interface Prefs {
  pushEnabled: boolean
  emailMode: number
  emailDigestHour: number
  vapidPublicKey: string | null
  pushSubscriptions: number
}

/** The browser's permission state, which is separate from Friday's own switch. */
type Permission = 'granted' | 'denied' | 'default' | 'unsupported'

function useNotificationPrefs() {
  const query = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api<Prefs>('/api/v1/me/notification-prefs'),
  })
  const qc = useQueryClient()
  const [permission, setPermission] = useState<Permission>('default')
  const [pending, setPending] = useState(false)

  // The permission state can change in the browser without Friday knowing
  // (the user can block the site in their browser settings), so it is read
  // from the browser rather than the server.
  useEffect(() => {
    setPermission(permissionState())
  }, [])

  const update = useCallback(
    async (patch: Partial<Prefs>) => {
      await api('/api/v1/me/notification-prefs', { method: 'PATCH', body: patch })
      await qc.invalidateQueries({ queryKey: ['notification-prefs'] })
    },
    [qc],
  )

  return { ...query, permission, pending, setPermission, update }
}

function BrowserChannelRow({ prefs }: { prefs: ReturnType<typeof useNotificationPrefs> }) {
  const { data, permission, pending, setPermission, update } = prefs
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const on = data?.pushEnabled ?? false

  async function toggle(next: boolean) {
    setBusy(true)
    setError(null)
    try {
      if (next) {
        const key = data?.vapidPublicKey
        if (!key) {
          setError('Push is not configured on this server yet.')
          return
        }
        // The permission request has to happen inside the click handler —
        // Firefox and Chrome both refuse it otherwise.
        const granted = await enablePush(key)
        setPermission(permissionState())
        if (!granted) {
          setError(
            permissionState() === 'denied'
              ? 'Notifications are blocked for this site. Allow them in your browser settings, then try again.'
              : 'Your browser did not grant notification permission.',
          )
          return
        }
        await update({ pushEnabled: true })
      } else {
        await disablePush()
        setPermission(permissionState())
        await update({ pushEnabled: false })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change browser notifications.')
    } finally {
      setBusy(false)
    }
  }

  const unsupported = permission === 'unsupported'
  const blocked = permission === 'denied'

  return (
    <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div className="text-sm font-semibold">Browser notifications</div>
        <p className="mt-0.5 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
          A notification in Chrome or Firefox, even when Friday is closed.
        </p>
        {unsupported && (
          <p className="mt-1.5 text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
            This browser cannot receive push notifications.
          </p>
        )}
        {blocked && on && (
          <p className="mt-1.5 text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
            Notifications are blocked for this site in your browser. Allow them in your browser settings, then
            turn this off and on again.
          </p>
        )}
        {error && (
          <p className="mt-1.5 text-xs leading-relaxed text-[rgb(var(--fg-danger))]">{error}</p>
        )}
      </div>
      <Switch
        label="Browser notifications"
        checked={on}
        disabled={unsupported || busy}
        onChange={(next) => void toggle(next)}
        className="self-start sm:self-center"
      />
    </div>
  )
}

function EmailChannelRow({ prefs }: { prefs: ReturnType<typeof useNotificationPrefs> }) {
  const { data, update } = prefs
  const mode = data?.emailMode ?? 0
  const hour = data?.emailDigestHour ?? 8

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Email</div>
          <p className="mt-0.5 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
            A message when something needs you — no inbox noise.
          </p>
        </div>
        <Switch
          label="Email notifications"
          checked={mode !== 0}
          onChange={(next) => void update({ emailMode: next ? 1 : 0 })}
          className="self-start sm:self-center"
        />
      </div>

      {mode !== 0 && (
        <div className="flex flex-col gap-2 border-l-[3px] border-[rgb(var(--border))] pl-3 sm:flex-row sm:items-center sm:gap-4">
          <span className="text-xs font-semibold">When</span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void update({ emailMode: 1 })}
              className={cn(
                'border-[3px] border-[rgb(var(--border))] px-3 py-1.5 text-xs font-semibold',
                mode === 1
                  ? 'bg-[rgb(var(--accent))] text-[rgb(var(--accent-fg))]'
                  : 'bg-[rgb(var(--bg-elevated))] text-[rgb(var(--fg))]',
              )}
            >
              Immediately
            </button>
            <button
              type="button"
              onClick={() => void update({ emailMode: 2 })}
              className={cn(
                'border-[3px] border-[rgb(var(--border))] px-3 py-1.5 text-xs font-semibold',
                mode === 2
                  ? 'bg-[rgb(var(--accent))] text-[rgb(var(--accent-fg))]'
                  : 'bg-[rgb(var(--bg-elevated))] text-[rgb(var(--fg))]',
              )}
            >
              Daily report
            </button>
            {mode === 2 && (
              <label className="flex items-center gap-2 text-xs text-[rgb(var(--fg-muted))]">
                Sent at
                <select
                  value={hour}
                  onChange={(e) => void update({ emailDigestHour: Number(e.target.value) })}
                  className="border-[3px] border-[rgb(var(--border))] bg-[rgb(var(--bg-elevated))] px-2 py-1 text-xs text-[rgb(var(--fg))]"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, '0')}:00 UTC
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ChannelRow({
  title,
  description,
  badge,
  muted,
}: {
  title: string
  description: string
  badge: string
  muted?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <p className="mt-0.5 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
          {description}
        </p>
      </div>
      <span
        className={cn(
          'badge shrink-0 border text-[10px]',
          muted
            ? 'border-[rgb(var(--border))] text-[rgb(var(--fg-muted))]'
            : 'border-[rgb(var(--accent))] bg-[rgb(var(--accent))] text-[rgb(var(--accent-fg))]',
        )}
      >
        {badge}
      </span>
    </div>
  )
}

/* ─────────────────────────────── pieces ─────────────────────────────── */

function SectionHead({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-1 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">{description}</p>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium">{label}</span>
      {hint ? <span className="mt-0.5 block text-[11px] text-[rgb(var(--fg-muted))]">{hint}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}
